import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';

const ConsentForm = ({ onConsent }) => {
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!accepted || !signature.trim()) {
      toast.error('Por favor, acepta los términos y proporciona tu firma');
      return;
    }

    setSubmitting(true);
    try {
      const api = createAuthAPI();
      await api.post('/client/consent', { firma_digital: signature });
      toast.success('Consentimiento aceptado exitosamente');
      onConsent();
    } catch (error) {
      toast.error('Error al procesar consentimiento');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pharmacy-gradient flex items-center justify-center p-4">
      <Card className="max-w-3xl w-full pharmacy-card" data-testid="consent-form">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <img src="/logo.jpg" alt="Farmacia Tarin" className="h-24 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-800 text-center">
            Consentimiento de Protección de Datos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
            <h3 className="font-bold text-lg mb-4">Política de Protección de Datos - Farmacia Tarin</h3>
            
            <p className="text-sm text-gray-700 mb-4">
              En cumplimiento con el Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica de Protección de Datos (LOPD), 
              Farmacia Tarin se compromete a proteger la privacidad y seguridad de sus datos personales.
            </p>

            <h4 className="font-semibold mb-2">Datos que recopilamos:</h4>
            <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1">
              <li>Nombre completo y fecha de nacimiento</li>
              <li>Datos de contacto (teléfono, dirección, correo electrónico)</li>
              <li>Datos de salud relacionados con seguimiento nutricional (peso, altura, glucemia, colesterol)</li>
              <li>Historial de valoraciones de recetas y videos visualizados</li>
            </ul>

            <h4 className="font-semibold mb-2">Finalidad del tratamiento:</h4>
            <ul className="list-disc list-inside text-sm text-gray-700 mb-4 space-y-1">
              <li>Seguimiento de su programa de nutrición y control de peso</li>
              <li>Personalización de su plan de dieta y ejercicios</li>
              <li>Comunicaciones relacionadas con su tratamiento</li>
              <li>Análisis de su evolución y progreso</li>
            </ul>

            <h4 className="font-semibold mb-2">Sus derechos:</h4>
            <p className="text-sm text-gray-700 mb-4">
              Usted tiene derecho a acceder, rectificar, suprimir, limitar el tratamiento, oponerse al tratamiento 
              y solicitar la portabilidad de sus datos. Para ejercer estos derechos, contacte con Farmacia Tarin.
            </p>

            <h4 className="font-semibold mb-2">Seguridad:</h4>
            <p className="text-sm text-gray-700 mb-4">
              Sus datos están protegidos mediante medidas de seguridad técnicas y organizativas apropiadas.
              Solo el personal autorizado de Farmacia Tarin tendrá acceso a su información.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="consent"
                checked={accepted}
                onCheckedChange={setAccepted}
                data-testid="consent-checkbox"
              />
              <label
                htmlFor="consent"
                className="text-sm leading-relaxed cursor-pointer"
              >
                He leído y acepto la política de protección de datos. Autorizo a Farmacia Tarin a recopilar y 
                tratar mis datos personales y de salud para los fines indicados.
              </label>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Firma Digital (Escriba su nombre completo):</label>
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Su nombre y apellidos"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="signature-input"
              />
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!accepted || !signature.trim() || submitting}
              className="w-full pharmacy-button text-white py-6 text-lg"
              data-testid="submit-consent-button"
            >
              {submitting ? 'Procesando...' : 'Aceptar y Continuar'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsentForm;